const toPeriodStats = (source = {}) => {
  const stats = {
    pages: Number(source.pages) || 0,
    books: Number(source.books) || 0,
  };

  if (source.streakDays !== undefined && source.streakDays !== null) {
    stats.streakDays = Number(source.streakDays) || 0;
  }

  return stats;
};

export const getPeriodStats = (student, periodKey) => {
  return toPeriodStats(student?.[periodKey] || {});
};

export const MAX_VISIBLE_STARS = 10;
export const STARS_PER_MILESTONE_BADGE = 10;

export const getGradeLevelFromClassName = (className) => {
  const match = String(className || '').match(/\d+/);
  const level = match ? Number(match[0]) : 1;
  return Number.isFinite(level) && level > 0 ? level : 1;
};

const getSafeGradeLevel = (gradeLevel = 1) => {
  const safeGrade = Number(gradeLevel);
  return Number.isFinite(safeGrade) && safeGrade > 0 ? safeGrade : 1;
};

export const getStarPageUnit = (gradeLevel = 1) => {
  const safeGrade = getSafeGradeLevel(gradeLevel);
  return 50 * safeGrade;
};

export const getSuperBadgePageThreshold = (gradeLevel = 1) => {
  const safeGrade = getSafeGradeLevel(gradeLevel);
  return 1000 * safeGrade;
};

export const getMarathonBadgePageThreshold = (gradeLevel = 1) => {
  const safeGrade = getSafeGradeLevel(gradeLevel);
  return 300 * safeGrade;
};

export const calcStars = ({ pages = 0 }, gradeLevel = 1) => {
  const pageUnit = getStarPageUnit(gradeLevel);
  return Math.floor((Number(pages) || 0) / pageUnit);
};

export const getMilestoneBadgeInfo = (student, gradeLevel = 1) => {
  const allStats = getPeriodStats(student, 'schoolYear');
  const weekStats = getPeriodStats(student, 'week');

  const safeAllPages = Math.max(0, Number(allStats.pages) || 0);
  const safeWeekPages = Math.max(0, Number(weekStats.pages) || 0);
  const weekStartAllPages = Math.max(0, safeAllPages - safeWeekPages);

  const totalStars = calcStars({ pages: safeAllPages }, gradeLevel);
  const weekStartStars = calcStars({ pages: weekStartAllPages }, gradeLevel);

  const badgeCount = Math.floor(totalStars / STARS_PER_MILESTONE_BADGE);
  const weekStartBadgeCount = Math.floor(weekStartStars / STARS_PER_MILESTONE_BADGE);
  const gainedCountThisWeek = Math.max(0, badgeCount - weekStartBadgeCount);

  return {
    badgeCount,
    gainedThisWeek: gainedCountThisWeek > 0,
    gainedCountThisWeek,
    totalStars,
  };
};

export const getPagesToNextStar = ({ pages = 0 }, gradeLevel = 1) => {
  const pageUnit = getStarPageUnit(gradeLevel);
  const safePages = Math.max(0, Number(pages) || 0);
  const usedInCurrentStep = safePages % pageUnit;
  if (usedInCurrentStep === 0) return pageUnit;
  return pageUnit - usedInCurrentStep;
};

export const clampStarsForDisplay = (starCount, maxStars = MAX_VISIBLE_STARS) => {
  const safe = Number(starCount) || 0;
  return Math.max(0, Math.min(safe, maxStars));
};

export const getLevelLabel = (stars) => {
  if (stars >= 10) return '🏆 Kitap Kurdu';
  if (stars >= 7) return '📚 Kitap Dostu';
  if (stars >= 4) return '📘 Okur';
  return '🌱 Okuma Yolunda';
};

const getPreviousSamePeriodStats = (student, periodKey) => {
  if (periodKey === 'week' && student?.previousWeek) {
    return toPeriodStats(student.previousWeek);
  }
  if (periodKey === 'month' && student?.previousMonth) {
    return toPeriodStats(student.previousMonth);
  }
  return null;
};

export const getImprovement = (student, periodKey) => {
  const previous = getPreviousSamePeriodStats(student, periodKey);
  if (!previous) return null;

  const current = getPeriodStats(student, periodKey);

  return {
    deltaPages: current.pages - previous.pages,
    deltaBooks: current.books - previous.books,
  };
};

export const getTopGrowthStudentId = (students, periodKey) => {
  if (periodKey !== 'week' && periodKey !== 'month') return null;

  const candidates = (students || [])
    .map((student) => ({
      student,
      improvement: getImprovement(student, periodKey),
    }))
    .filter((item) => item.improvement);

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    if (b.improvement.deltaPages !== a.improvement.deltaPages) {
      return b.improvement.deltaPages - a.improvement.deltaPages;
    }
    if (b.improvement.deltaBooks !== a.improvement.deltaBooks) {
      return b.improvement.deltaBooks - a.improvement.deltaBooks;
    }
    return (a.student.firstName || '').localeCompare(b.student.firstName || '', 'tr-TR');
  });

  return candidates[0].student.id;
};

const badge = (key, label, icon, mult) => ({ key, label, icon, mult });

export const getCardBadges = ({ student, periodKey, isTopGrowth = false, gradeLevel = 1 }) => {
  const { pages, books, streakDays } = getPeriodStats(student, periodKey);
  const priorityBadges = [];
  const superPageThreshold = getSuperBadgePageThreshold(gradeLevel);
  const marathonPageThreshold = getMarathonBadgePageThreshold(gradeLevel);

  if (isTopGrowth) priorityBadges.push(badge('growth', 'Gelişen', '📈', 1));

  if (streakDays >= 7) priorityBadges.push(badge('series', 'Seri', '🔥', 3));
  else if (streakDays >= 5) priorityBadges.push(badge('regular', 'Düzenli', '📘', 2));
  else if (streakDays >= 3) priorityBadges.push(badge('regular', 'Düzenli', '📘', 1));

  if (books >= 5) priorityBadges.push(badge('friend', 'Dost', '📚', 3));
  else if (books >= 3) priorityBadges.push(badge('finisher', 'Bitirici', '📖', 2));
  else if (books === 2) priorityBadges.push(badge('finisher', 'Bitirici', '📖', 1));

  if (periodKey === 'schoolYear' && pages >= superPageThreshold) {
    priorityBadges.push(badge('super', 'Süper', '🚀', 1));
  } else if (periodKey === 'month' && pages >= marathonPageThreshold) {
    priorityBadges.push(badge('marathon', 'Maraton', '🏔️', 1));
  }

  return priorityBadges;
};
